import { AdminUnseenProvider } from "@/hooks/useAdminUnseen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TechnicalDetailsDialog } from "@/components/system/TechnicalDetailsDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import OpenHouseBanner from "@/components/OpenHouseBanner";
import CanonicalUrl from "@/components/CanonicalUrl";
import { AdminGuard } from "@/components/admin/AdminGuard";
import Footer from "@/components/Footer";
import Index from "./pages/Index";
import Classes from "./pages/Classes";
import CalendarPage from "./pages/CalendarPage";
import Teachers from "./pages/Teachers";
import PaymentSuccess from "./pages/PaymentSuccess";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import Unsubscribe from "./pages/Unsubscribe";
import PopUpPage from "./pages/PopUpPage";
import OAuthConsent from "./pages/OAuthConsent";
import Login from "./pages/Login";
import UnifiedDashboard from "./pages/UnifiedDashboard";
import RegistrationsPage from "./pages/admin/RegistrationsPage";
import PopupAdminPage from "./pages/admin/PopupPage";
import ClassesAdminPage from "./pages/admin/ClassesPage";
import InstructorsAdminPage from "./pages/admin/InstructorsPage";
import ReportsPage from "./pages/admin/ReportsPage";
import FinancialLedgerPage from "./pages/admin/FinancialLedgerPage";
import SettingsPage from "./pages/admin/SettingsPage";
import AcademicCalendarPage from "./pages/admin/AcademicCalendarPage";
import WantToTeach from "./pages/WantToTeach";
import InstructorOnboarding from "./pages/InstructorOnboarding";
import WaitingPage from "./pages/admin/WaitingPage";
import ActionCenter from "./pages/admin/ActionCenter";
import KnowledgeVaultPage from "./pages/admin/KnowledgeVaultPage";
import ArchivedAlertsPage from "./pages/admin/ArchivedAlertsPage";
import PayrollPage from "./pages/admin/PayrollPage";
import ReferralsPage from "./pages/admin/ReferralsPage";
import AccountCreditsPage from "./pages/admin/AccountCreditsPage";
import PayrollRulesPage from "./pages/admin/PayrollRulesPage";
import GreenhousePage from "./pages/admin/GreenhousePage";
import PlaybookPage from "./pages/admin/PlaybookPage";
import OpenHouse from "./pages/OpenHouse";
import OpenHouseAdminPage from "./pages/admin/OpenHousePage";
import ContentStudioPage from "./pages/admin/ContentStudioPage";
import EosBuilderPage from "./pages/admin/EosBuilderPage";
import TaxCenterPage from "./pages/admin/TaxCenterPage";
import EnrollmentAlertsPage from "./pages/admin/EnrollmentAlertsPage";
import { hydrateClassIdentityFromDb } from "@/lib/classIdentity";
import { supabase } from "@/integrations/supabase/client";

// Merge live DB slug↔uuid pairs into the class-identity registry so
// enrollment counts stay correct even if new classes drift between
// slug-keyed and uuid-keyed storage. Seed data already covers today's set.
void hydrateClassIdentityFromDb(supabase);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Returning to a backgrounded tab must not refetch every admin query and
      // repaint records over in-progress edits. Data still refreshes on mount,
      // on invalidation and via explicit refetches.
      refetchOnWindowFocus: false,
      // Never hammer a protected endpoint while auth is still settling.
      retry: (failureCount, error) => {
        const msg = String((error as any)?.message ?? "");
        if (msg.includes("not_signed_in") || msg.includes("sign in")) return false;
        return failureCount < 1;
      },
    },
  },
});


// Every admin route shares one per-administrator "unseen items" provider, so
// sidebar bubbles and in-page review bars agree on the same read state.
const admin = (el: JSX.Element) => (
  <AdminGuard>
    <AdminUnseenProvider>{el}</AdminUnseenProvider>
  </AdminGuard>
);



const Layout = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isBare = location.pathname === "/login" || location.pathname === "/.lovable/oauth/consent";
  const hideChrome = isAdmin || isBare;

  return (
    <>
      <CanonicalUrl />
      {!hideChrome && <OpenHouseBanner />}
      {!hideChrome && <Navbar />}
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/classes" element={<Classes />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/teachers" element={<Teachers />} />
          <Route path="/pop-up" element={<PopUpPage />} />
          <Route path="/open-house" element={<OpenHouse />} />
          <Route path="/want-to-teach" element={<WantToTeach />} />
          <Route path="/instructor-onboarding" element={<InstructorOnboarding />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={admin(<UnifiedDashboard />)} />
          <Route path="/admin/greenhouse" element={admin(<GreenhousePage />)} />
          <Route path="/admin/waiting" element={admin(<WaitingPage />)} />
          <Route path="/admin/action-center" element={admin(<ActionCenter />)} />
          <Route path="/admin/knowledge" element={admin(<KnowledgeVaultPage />)} />
          <Route path="/admin/archived-alerts" element={admin(<ArchivedAlertsPage />)} />
          <Route path="/admin/registrations" element={admin(<RegistrationsPage />)} />
          <Route path="/admin/classes" element={admin(<ClassesAdminPage />)} />
          <Route path="/admin/instructors" element={admin(<InstructorsAdminPage />)} />
          <Route path="/admin/enrollment-alerts" element={admin(<EnrollmentAlertsPage />)} />
          {/* Compatibility redirect: staffing was consolidated into /admin/classes. */}
          <Route path="/admin/staffing" element={<Navigate to="/admin/classes" replace />} />

          <Route path="/admin/tax" element={admin(<TaxCenterPage />)} />
          <Route path="/admin/popup" element={admin(<PopupAdminPage />)} />
          <Route path="/admin/reports" element={admin(<ReportsPage />)} />
          <Route path="/admin/ledger" element={admin(<FinancialLedgerPage />)} />
          <Route path="/admin/payroll" element={admin(<PayrollPage />)} />
          <Route path="/admin/referrals" element={admin(<ReferralsPage />)} />
          <Route path="/admin/account-credits" element={admin(<AccountCreditsPage />)} />
          <Route path="/admin/settings" element={admin(<SettingsPage />)} />
          <Route path="/admin/settings/academic-calendar" element={admin(<AcademicCalendarPage />)} />
          <Route path="/admin/settings/payroll-rules" element={admin(<PayrollRulesPage />)} />
          <Route path="/admin/playbook" element={admin(<PlaybookPage />)} />
          <Route path="/admin/open-house" element={admin(<OpenHouseAdminPage />)} />
          <Route path="/admin/studio/content" element={admin(<ContentStudioPage />)} />
          <Route path="/admin/studio/builder" element={admin(<EosBuilderPage />)} />
          {/* Retired: the legacy /admin/manage dashboard now lives on the
              canonical Registrations page. Bookmarks keep working. */}
          <Route path="/admin/manage" element={<Navigate to="/admin/registrations" replace />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <TechnicalDetailsDialog />
      <BrowserRouter>
        <AuthProvider>
          <Layout />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
